import type { ActivityLogEntry } from "@/api/activity";
import type { Notification } from "@/api/notifications";
import type { WsMessage } from "@/api/websocket";
import { handleLiveTalkPromptReceipt } from "@/desktop/live-talk-prompt-receipt";
import { startQuietTurnCheck } from "@/desktop/tts-quiet-turns";
import {
  adoptConversationSplit,
  type ConversationSplit,
} from "@/lib/accepted-new-conversation-route";
import { applyProjectSwitchRequest } from "@/lib/apply-space-switch-request";
import { applyPromptDraftTopic } from "@/lib/prompt-draft-topic";
import { applyRuntimeCompaction } from "@/lib/runtime-compaction-event";
import {
  applyPromptReceipt,
  type PromptReceiptPayload,
} from "@/lib/prompt-receipt-bus";
import { payloadString } from "@/lib/work-toast-item";
import { toActivityEntry } from "@/stores/activity-store";
import { makeLogEntry, useDebugLogStore } from "@/stores/debug-log-store";
import { useDirectApprovalSettledStore } from "@/stores/direct-approval-settled-store";
import { applyAgentHeartbeatSuspended } from "./apply-agent-heartbeat-event";
import { applyDirectSessionProgress } from "./direct-session-progress-event";
import { isRemoteDeviceAction } from "./remote-device-frame";
import { silentTtsStore } from "./silent-tts-store";

import {
  applyAwaitingInputEvent,
  applyCursorEvent,
} from "./companion-frame-reducer-cursor";
import { applyExecutionEvent } from "./companion-frame-reducer-execution";
import {
  applyCompanionStatusEvent,
  applyFrameEvent,
} from "./companion-frame-reducer-frames";
import { applyMessageEvent } from "./companion-frame-reducer-message";
import type { FrameApplyContext } from "./companion-frame-reducer-shared";
import { pushConversationActivity } from "./companion-frame-reducer-shared";
import {
  applyRuntimeEvent,
  applyTaskEvent,
} from "./companion-frame-reducer-task-runtime";
import { debugToolsEnabled } from "@/lib/release-channel";
export { awaitingInputTtsSource } from "./companion-frame-reducer-cursor";
export type { FrameApplyContext } from "./companion-frame-reducer-shared";
// ── Main dispatcher ────────────────────────────────────────────

export function applyCompanionFrame(
  msg: WsMessage,
  ctx: FrameApplyContext,
): void {
  if (isRemoteDeviceAction(msg)) return;
  if (msg.device_local === false) ctx = { ...ctx, ttsStore: silentTtsStore };
  switch (msg.type) {
    case "frame.creating":
    case "frame.created":
    case "frame.updated":
    case "frame.deleted":
      applyFrameEvent(msg, ctx);
      break;

    case "companion.status":
    case "desktop.update_available":
      applyCompanionStatusEvent(msg, ctx);
      break;

    case "execution.status":
    case "execution.deferred":
    case "execution.tasks":
    case "execution.resource":
    case "execution.warning":
      applyExecutionEvent(msg, ctx);
      break;

    case "task.refined":
    case "task.status_changed":
    case "task.created":
    case "task.blocked":
    case "task.unblocked":
    case "task.updated":
    case "task.comment.created":
      applyTaskEvent(msg, ctx);
      break;

    case "agent.heartbeat_suspended":
      applyAgentHeartbeatSuspended(msg, ctx);
      break;

    case "cursor.move":
    case "cursor.question":
      applyCursorEvent(msg, ctx);
      break;

    case "awaiting_input.requested":
    case "awaiting_input.resolved":
    case "awaiting_input.answered":
      applyAwaitingInputEvent(msg, ctx);
      break;

    case "runtime.tokens":
      applyRuntimeEvent(msg, ctx);
      break;

    case "space.renamed": {
      const { space_id, name } = msg.data as {
        space_id?: string;
        name?: string;
      };
      const proj = ctx.projectStore.getState().currentProject;
      if (space_id && name && proj) {
        ctx.projectStore.setState((s) => ({
          currentSpace:
            s.currentSpace?.id === space_id
              ? { ...s.currentSpace, name }
              : s.currentSpace,
          spaces: s.spaces.map((sp) =>
            sp.id === space_id ? { ...sp, name } : sp,
          ),
          allProjectSpaces: {
            ...s.allProjectSpaces,
            [proj.id]: (s.allProjectSpaces[proj.id] ?? []).map((sp) =>
              sp.id === space_id ? { ...sp, name } : sp,
            ),
          },
        }));
      }
      break;
    }

    case "project.switch_requested":
    case "space.switch_requested":
      applyProjectSwitchRequest(msg, ctx);
      break;

    case "project.renamed": {
      const { project_id, name } = msg.data as {
        project_id?: string;
        name?: string;
      };
      if (project_id && name) {
        ctx.projectStore.setState((s) => ({
          projects: s.projects.map((p) =>
            p.id === project_id ? { ...p, name } : p,
          ),
          currentProject:
            s.currentProject?.id === project_id
              ? { ...s.currentProject, name }
              : s.currentProject,
        }));
      }
      break;
    }

    case "workspace.changed":
      if (ctx.ownsSideEffects !== false) {
        void ctx.projectStore.getState().fetchAllProjects(true);
        // A new, restored or template-built project arrives with tasks no
        // event announced; the boards' one list reloads to hold them.
        const tasks = ctx.tasksStore.getState();
        if (tasks.loaded) void tasks.fetchTasks();
      }
      break;

    case "activity.created": {
      const activity = msg.data as unknown as ActivityLogEntry;
      ctx.activityStore.getState().pushEntry(toActivityEntry(activity));
      pushConversationActivity(activity, ctx);
      const action = activity.action;
      if (action === "task.created" || action === "task.completed")
        ctx.tasksStore.getState().triggerRefetch();
      break;
    }

    case "permission.directory_required": {
      const pd = msg.data as Record<string, unknown>;
      ctx.canvasStore.getState().addPendingPermission({
        executionId: pd.execution_id as string,
        blockedDirectories: pd.blocked_directories as string[],
        allDirectories: pd.all_directories as string[],
      });
      break;
    }

    case "notification.new":
      ctx.notificationStore
        .getState()
        .pushNotification(msg.data as unknown as Notification);
      break;

    case "notification.read":
      ctx.notificationStore
        .getState()
        .applyServerRead((msg.data as { ids: string[] }).ids);
      break;

    // A tool approval's card is a work toast (`work-toast-tool-approvals`);
    // Needs you counts it.
    case "error_log.new":
    case "direct_session.approval_requested":
      ctx.notificationStore.getState().refreshSummarySoon();
      break;

    case "message.created":
      applyMessageEvent(msg, ctx);
      break;

    case "runtime.state": {
      const { agent_key, state } = msg.data as {
        agent_key: string;
        state: string;
      };
      const projectScopeIdForState =
        (msg.data as { project_id?: string }).project_id ??
        (msg.data as { space_id?: string }).space_id ??
        ctx.projectStore.getState().currentProject?.id ??
        ctx.projectStore.getState().currentSpace?.id ??
        "";
      if (state === "idle") {
        ctx.companionStore
          .getState()
          .clearAgentSession(agent_key, projectScopeIdForState);
      } else if (state === "active") {
        const executionId =
          (msg.data as { execution_id?: string }).execution_id ?? "";
        const sessionIdFromMsg =
          (msg.data as { runtime_session_id?: string }).runtime_session_id ||
          undefined;
        const conversationIdFromMsg =
          (msg.data as { conversation_id?: string }).conversation_id ||
          undefined;
        const runtimeConversationFrameId =
          (msg.data as { conversation_frame_id?: string })
            .conversation_frame_id || undefined;
        ctx.companionStore
          .getState()
          .setAgentSession(
            agent_key,
            projectScopeIdForState,
            executionId,
            sessionIdFromMsg,
            conversationIdFromMsg,
            runtimeConversationFrameId,
          );
      }
      break;
    }

    case "conversations.changed": {
      // Clients that did not send the prompt follow the split here too.
      const split = msg.data?.conversation_split as
        | ConversationSplit
        | undefined;
      if (split)
        adoptConversationSplit({
          client_request_id: null,
          conversation_id: split.to,
          conversation_split: split,
        });
      window.dispatchEvent(new Event("denker-conversations-changed"));
      break;
    }

    case "agents.changed": {
      const projectId = msg.data?.project_id as string | undefined;
      if (projectId) ctx.agentStore.getState().fetchProjectAgents(projectId);
      break;
    }

    case "prompt.draft.topic":
      applyPromptDraftTopic(msg.data ?? {});
      break;

    case "prompt.receipt": {
      const receipt = msg.data as unknown as PromptReceiptPayload;
      applyPromptReceipt(receipt);
      // Asked over music, a video or a call, its answer stays quiet (`tts-quiet-turns.ts`).
      const answeredBy =
        receipt.steered_into_execution_id ?? receipt.execution_id;
      if (answeredBy) startQuietTurnCheck(answeredBy);
      handleLiveTalkPromptReceipt(receipt);
      if (receipt.execution_id && receipt.steered_into_execution_id)
        ctx.conversationStore
          .getState()
          .markSteered(receipt.execution_id, receipt.steered_into_execution_id);
      break;
    }

    case "runtime.compaction":
      applyRuntimeCompaction(msg.data);
      break;

    case "direct_session.progress":
      applyDirectSessionProgress(msg.data, ctx);
      break;

    case "direct_session.approval_settled":
      useDirectApprovalSettledStore.getState().apply(msg.data);
      break;

    case "integration.connection_changed": {
      // Pull lazily to avoid a circular dep between this reducer and
      // the integration store (which depends on the notification store
      // that this reducer also touches).
      void import("@/stores/integration-store").then(
        ({ useIntegrationStore }) => {
          useIntegrationStore
            .getState()
            .actions.handleWebSocketEvent(
              msg.data as unknown as import("@/stores/integration-store").ConnectionChangedEvent,
            );
        },
      );
      break;
    }

    case "error": {
      console.error("[canvas] Server error:", msg.data.message);
      if (debugToolsEnabled()) {
        useDebugLogStore
          .getState()
          .push(
            makeLogEntry(
              "error",
              "backend",
              "server",
              String(msg.data.message ?? "Unknown server error"),
            ),
          );
      }
      // A stop rejected as historical means the pill tracks a superseded run
      // the server will never settle: remove it instead of leaving a working
      // pill no stop can clear — nothing says it failed or was stopped.
      // Pending user decisions are never auto-resolved.
      {
        const data = msg.data as
          | { error?: unknown; message?: unknown; execution_id?: unknown }
          | undefined;
        const message = typeof data?.message === "string" ? data.message : "";
        if (
          data?.error === "native_subagent_control_rejected" &&
          message.toLowerCase().includes("historical")
        ) {
          const key = payloadString(
            data as Record<string, unknown>,
            "execution_id",
          );
          const item = key
            ? ctx.workToastStore.getState().items[key]
            : undefined;
          if (item && item.state === "working") {
            ctx.workToastStore.getState().removeItem(item.key);
          }
        }
      }
      break;
    }
  }
}
